import { ChangeDetectionStrategy, Component, ElementRef, inject, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { filter } from 'rxjs/operators';
import { AuthService } from '@core/authentication';
import { AuthWindowComponent } from '../auth-window/auth-window.component';

const REMEMBER_KEY = 'admin_login_rememberMe';
const USERNAME_KEY = 'admin_login_username';

/** Login (Club-Manager-Login-Spec, states "Anmelden" and "Fehler"). */
@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, RouterLink, MatIcon, MatTooltip, TranslateModule, AuthWindowComponent],
})
export class LoginComponent {
  private readonly fb = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly auth = inject(AuthService);
  private readonly translate = inject(TranslateService);

  private readonly passwordInput = viewChild<ElementRef<HTMLInputElement>>('passwordInput');

  readonly submitting = signal(false);
  readonly showPassword = signal(false);
  /** Error above the form (spec 4, "Fehler"); never reveals whether the user exists. */
  readonly error = signal<string | null>(null);
  /** Came here because the session ran out (SessionExpiryService). */
  readonly expired = signal(this.route.snapshot.queryParamMap.has('expired'));

  readonly form = this.fb.nonNullable.group({
    username: [readSession(USERNAME_KEY) ?? '', [Validators.required]],
    password: ['', [Validators.required]],
    rememberMe: [readSession(REMEMBER_KEY) === 'true'],
  });

  constructor() {
    // Typing again ends the error state of the fields.
    this.form.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => this.error.set(null));
  }

  invalid(name: 'username' | 'password'): boolean {
    const control = this.form.controls[name];
    return (control.invalid && control.touched) || this.error() !== null;
  }

  login(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const { username, password, rememberMe } = this.form.getRawValue();
    this.rememberUsername(username, rememberMe);
    this.submitting.set(true);
    this.error.set(null);
    this.expired.set(false);

    this.auth
      .login(username.trim(), password, rememberMe)
      .pipe(filter(authenticated => authenticated))
      .subscribe({
        next: () => this.router.navigateByUrl(this.returnUrl()),
        error: (response: HttpErrorResponse) => {
          this.submitting.set(false);
          // Spec 8: the focus jumps into the (emptied) password field.
          this.form.controls.password.reset('', { emitEvent: false });
          this.error.set(this.message(response));
          setTimeout(() => this.passwordInput()?.nativeElement.focus());
        },
      });
  }

  /** Page the expired session was on; app paths only, never another site. */
  private returnUrl(): string {
    const url = this.route.snapshot.queryParamMap.get('returnUrl') ?? '';
    return url.startsWith('/') && !url.startsWith('//') && !url.startsWith('/\\') && !url.startsWith('/auth/') ? url : '/';
  }

  private message(response: HttpErrorResponse): string {
    const body = response.error ?? {};
    if (response.status === 429) {
      const minutes = Math.max(1, Math.ceil((body.retryAfter ?? (body.lockoutMinutes ?? 15) * 60) / 60));
      return this.translate.instant('auth.error_locked', { minutes });
    }
    if (typeof body.remainingAttempts === 'number') {
      return this.translate.instant('auth.error_credentials', {
        n: body.remainingAttempts,
        minutes: body.lockoutMinutes ?? 15,
      });
    }
    if (response.status === 400 && body.message) {
      // e.g. deactivated account
      return body.message;
    }
    return this.translate.instant('auth.error_generic');
  }

  private rememberUsername(username: string, remember: boolean): void {
    try {
      if (remember) {
        sessionStorage.setItem(REMEMBER_KEY, 'true');
        sessionStorage.setItem(USERNAME_KEY, username);
      } else {
        sessionStorage.removeItem(REMEMBER_KEY);
        sessionStorage.removeItem(USERNAME_KEY);
      }
    } catch {
      // Storage not available: nothing to remember.
    }
  }
}

function readSession(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}
